import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Form, FormItem, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { PasswordInput } from "./password-input"
import { safeZodResolver, onFormValidationError } from "@/utils/safe-zod-resolver"
import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { api } from "@/utils/api-client"

// Validation schema for email submission
const emailSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
})

// Validation schema for password reset
const resetPasswordSchema = z.object({
  tempPassword: z
    .string()
    .min(1, "Temporary password is required"),
  newPassword: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
  confirmPassword: z
    .string()
    .min(1, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

type EmailFormData = z.infer<typeof emailSchema>
type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

/**
 * Forgot password form component
 * Handles both email submission and password reset steps
 */
export function ForgotPasswordForm() {
  const navigate = useNavigate()
  const [step, setStep] = useState<"email" | "reset">("email")
  const [isLoading, setIsLoading] = useState(false)
  const [userEmail, setUserEmail] = useState("")

  // Email form
  const emailForm = useForm<EmailFormData>({
    resolver: safeZodResolver(emailSchema as any),
    defaultValues: {
      email: "",
    },
  })

  // Password reset form
  const resetForm = useForm<ResetPasswordFormData>({
    resolver: safeZodResolver(resetPasswordSchema as any),
    defaultValues: {
      tempPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  })

  const onEmailSubmit = async (data: EmailFormData) => {
    setIsLoading(true)
    try {
      await api.post('/auth/forgot-password', { email: data.email }, {
        requiresAuth: false,
        showSuccessToast: false,
        showErrorToast: false,
      })

      setUserEmail(data.email)
      setStep("reset")
      toast.success("Temporary password sent to your email")
    } catch (error: any) {
      if (error?.response?.data?.error?.type === 'USER_NOT_FOUND') {
        emailForm.setError("email", { 
          message: "No account found with this email address" 
        })
      } else {
        toast.error(error?.message || "Failed to send temporary password")
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onResetSubmit = async (data: ResetPasswordFormData) => {
    setIsLoading(true)
    try {
      await api.post('/auth/reset-password', {
        email: userEmail,
        token: data.tempPassword,
        password: data.newPassword,
      }, {
        requiresAuth: false,
        showSuccessToast: false,
        showErrorToast: false,
      })

      toast.success("Password reset successful!")
      navigate("/signin", { replace: true })
    } catch (error: any) {
      const errorType = error?.response?.data?.error?.type
      const errorMessage = error?.response?.data?.error?.message || "Failed to reset password"

      if (errorType === 'INVALID_TEMP_PASSWORD') {
        resetForm.setError("tempPassword", { message: errorMessage })
      } else if (errorType === 'PASSWORD_EXPIRED') {
        toast.error(errorMessage)
        setStep("email") // Go back to email step
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (step === "email") {
    return (
      <div className="w-full">
        <div className="text-left mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">
            Forgot your password?
          </h1>
          <p className="text-sm text-muted-foreground">
            Enter your email address and we'll send you a temporary password
          </p>
        </div>

        <Form {...emailForm}>
          <form onSubmit={emailForm.handleSubmit(onEmailSubmit, onFormValidationError)} className="space-y-4">
            <FormItem>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                {...emailForm.register("email")}
                disabled={isLoading}
              />
              <FormMessage>
                {emailForm.formState.errors.email?.message}
              </FormMessage>
            </FormItem>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? "Sending..." : "Send temporary password"}
            </Button>

            <div className="text-center text-sm">
              <Link
                to="/signin"
                className="text-primary hover:text-primary/80 underline-offset-4 hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          </form>
        </Form>
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="text-left mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">
          Reset your password
        </h1>
        <p className="text-sm text-muted-foreground">
          Enter the temporary password from your email and choose a new password
        </p>
      </div>

      <Form {...resetForm}>
        <form onSubmit={resetForm.handleSubmit(onResetSubmit, onFormValidationError)} className="space-y-4">
          <FormItem>
            <Label htmlFor="tempPassword">Temporary Password</Label>
            <Input
              id="tempPassword"
              type="text"
              placeholder="Enter temporary password from email"
              {...resetForm.register("tempPassword")}
              disabled={isLoading}
            />
            <FormMessage>
              {resetForm.formState.errors.tempPassword?.message}
            </FormMessage>
          </FormItem>

          <FormItem>
            <Label htmlFor="newPassword">New Password</Label>
            <PasswordInput
              id="newPassword"
              placeholder="Enter your new password"
              {...resetForm.register("newPassword")}
              disabled={isLoading}
            />
            <FormMessage>
              {resetForm.formState.errors.newPassword?.message}
            </FormMessage>
          </FormItem>

          <FormItem>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <PasswordInput
              id="confirmPassword"
              placeholder="Confirm your new password"
              {...resetForm.register("confirmPassword")}
              disabled={isLoading}
            />
            <FormMessage>
              {resetForm.formState.errors.confirmPassword?.message}
            </FormMessage>
          </FormItem>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? "Resetting..." : "Reset password"}
          </Button>

          <div className="text-center text-sm space-y-2">
            <button
              type="button"
              onClick={() => setStep("email")}
              className="text-primary hover:text-primary/80 underline-offset-4 hover:underline"
              disabled={isLoading}
            >
              Didn't receive the email? Send again
            </button>
            <br />
            <Link
              to="/signin"
              className="text-primary hover:text-primary/80 underline-offset-4 hover:underline"
            >
              Back to sign in
            </Link>
          </div>
        </form>
      </Form>
    </div>
  )
}